import { useState, useEffect, memo } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Off for now, and kept whole rather than deleted.
 *
 * The staff screen mark covers the same page, and running both put two textures over every surface
 * at once. This is the one that was turned off because it is the one that answers the weaker
 * question: a connection rather than an account. Nothing else changes — the component still builds,
 * still themes itself, and comes back by flipping this to true.
 *
 * While it is off no request is made for the address either, so a disabled watermark does not
 * quietly keep calling an external service on every page load.
 *
 * If it is ever switched back on, it needs what the staff screen mark grew: this layer picks its
 * colour from the theme and declares no background colour of its own, which are the two things that
 * let Dark Reader cancel the mark and paint over the panel respectively. `utils/screenGround.ts`
 * measures the ground, and `StaffScreenMark.tsx` shows how the layer is pinned; the README's
 * "Surviving the browser it is drawn in" has the measurements.
 */
const ENABLED = false;

const WATERMARK_SYMBOLS = ['.', ':', '⁝', '⁞', '…', '⋯', '⋰', '⋱', '◦', '‧']; // 0-9

const ipToEncodedSymbols = (ip: string) => {
  if (!ip || ip === 'anonymous') ip = '0.0.0.0';
  
  // 1. Convert to zero-padded decimal string
  const paddedIp = ip.split('.').map(part => part.padStart(3, '0')).join('');
  
  // 2. Map each digit to a symbol
  let encoded = '';
  for (const char of paddedIp) {
    const digit = parseInt(char, 10);
    encoded += WATERMARK_SYMBOLS[digit];
  }
  return encoded;
};

const WatermarkGrid = memo(({ watermark, isLight }: { watermark: string; isLight: boolean }) => {
    /*
     * Dark bg: faint white. Light bg: faint ink, at roughly three times the alpha.
     *
     * The asymmetry is not a fudge. Perceived contrast tracks the change relative to the
     * background it sits on, not the absolute one, so the same step disappears on a bright
     * surface. Measured over a glass card: white at 0.015 on the dark ground shifts luminance by
     * 2.2 against a background of 13, which reads clearly; ink at 0.04 on the light ground shifts
     * it by 6.3 against 248, six times the absolute step for a sixth of the relative one, which is
     * why light mode looked bare. 0.12 puts the shift at ~18 and the ratio within reach of dark's
     * without the grid starting to read as texture over body copy.
     *
     * Visibility is the point here, not just recoverability: both values survive a re-encoded
     * screenshot, but a mark nobody can see deters nobody.
     */
    const color = isLight ? '#0f172a' : '#ffffff';
    const opacity = isLight ? 0.12 : 0.015;
    return (
        <div className="absolute inset-0 flex flex-col flex-wrap h-[200vh]" style={{ opacity }}>
            {Array.from({ length: 5000 }).map((_, i) => (
                <span key={i} className="text-[1.375rem] font-mono select-none p-2" style={{ color }}>
                    {watermark[i % watermark.length]}
                </span>
            ))}
        </div>
    )
});

export const IPWatermark = () => {
  const [watermark, setWatermark] = useState('');
  const { theme } = useTheme();

  useEffect(() => {
    if (!ENABLED) return;
    fetch('https://api.ipify.org?format=json')
      .then(response => response.json())
      .then(data => {
        setWatermark(ipToEncodedSymbols(data.ip));
      })
      .catch(error => {
        console.error("Could not fetch IP for watermarking:", error);
        setWatermark(ipToEncodedSymbols('anonymous'));
      });
  }, []);

  if (!ENABLED || !watermark) return null;

  /*
   * Above everything, and portalled to the document so no stacking context can trap it.
   *
   * A watermark that only covers the page behind a modal marks the screenshots nobody worries
   * about and misses the ones that matter: the ban form, a case and its evidence, a transcript.
   * Rendered inside the app tree at z-1000 it lost to every `.modal-overlay` at z-9999, so this
   * is deliberately the highest layer in the app and the max the property allows — nothing is
   * ever meant to sit on top of it.
   */
  return createPortal(
    <div className="fixed inset-0 pointer-events-none z-[2147483647] overflow-hidden" aria-hidden="true">
      <WatermarkGrid watermark={watermark} isLight={theme === 'light'} />
    </div>,
    document.body,
  );
};
